import { PdfContract, QuickContract } from '../models/OrderContract';

export enum ContractListStatus {
  cancelled = 'cancelled',
  unsigned = 'unsigned',
  waitingOther = 'waitingOther',
  bothSignedRegret = 'bothSignedRegret',
  bothSignedLocked = 'bothSignedLocked',
  offlineSigned = 'offlineSigned',
}

const STATUS_LABELS: Record<ContractListStatus, string> = {
  [ContractListStatus.cancelled]: '已作废',
  [ContractListStatus.unsigned]: '待签',
  [ContractListStatus.waitingOther]: '你已签',
  [ContractListStatus.bothSignedRegret]: '可反悔',
  [ContractListStatus.bothSignedLocked]: '已生效',
  [ContractListStatus.offlineSigned]: '线下已签',
};

export const getContractStatusLabel = (status: ContractListStatus): string =>
  STATUS_LABELS[status];

export const getContractStatusColor = (status: ContractListStatus): string => {
  switch (status) {
    case ContractListStatus.cancelled:
      return '#999999';
    case ContractListStatus.unsigned:
      return '#faad14';
    case ContractListStatus.waitingOther:
      return '#1890ff';
    case ContractListStatus.bothSignedRegret:
      return '#ff4d4f';
    case ContractListStatus.bothSignedLocked:
      return '#52c41a';
    case ContractListStatus.offlineSigned:
      return '#722ed1';
    default:
      return '#8c8c8c';
  }
};

export const isPdfPreSigned = (contract: PdfContract) =>
  contract.pdfSignMode === 1;

const isCancelled = (status: number) => status === 2;

const toNonEmptyString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (value instanceof Date) return value.toISOString();
  return String(value).trim();
};

export const isQuickPosterSigned = (contract: QuickContract) =>
  toNonEmptyString(contract.posterSignTime).length > 0 &&
  toNonEmptyString(contract.posterSignUrl).length > 0;

export const isQuickSignerSigned = (contract: QuickContract) =>
  toNonEmptyString(contract.signerSignTime).length > 0 &&
  toNonEmptyString(contract.signerSignUrl).length > 0;

export const getPartySignStatusLabel = (signed: boolean) =>
  signed ? '已签署' : '待签署';

export const getPartySignStatusColor = (signed: boolean) =>
  signed ? '#52c41a' : '#999999';

export const getQuickPartyDisplayName = (
  contract: QuickContract,
  role: 'poster' | 'signer',
  currentUserId?: string,
) => {
  const userId = role === 'poster' ? contract.posterId : contract.signerId;
  const name = role === 'poster' ? contract.posterName : contract.signerName;
  if (currentUserId && userId === currentUserId) return '你';
  if (toNonEmptyString(name).length > 0) return name;
  return role === 'poster' ? '发起人' : '签署人';
};

export const getPdfPartyDisplayName = (
  contract: PdfContract,
  role: 'poster' | 'signer',
  currentUserId?: string,
) => {
  const userId = role === 'poster' ? contract.posterId : contract.signerId;
  const name = role === 'poster' ? contract.posterName : contract.signerName;
  if (currentUserId && userId === currentUserId) return '你';
  if (toNonEmptyString(name).length > 0) return name;
  return role === 'poster' ? '发起人' : '签署人';
};

const isQuickFullySigned = (contract: QuickContract) =>
  isQuickPosterSigned(contract) && isQuickSignerSigned(contract);

const hasCurrentUserSignedQuick = (
  contract: QuickContract,
  currentUserId?: string,
) => {
  if (!currentUserId) return false;
  if (contract.posterId === currentUserId) return isQuickPosterSigned(contract);
  if (contract.signerId === currentUserId) return isQuickSignerSigned(contract);
  return false;
};

export const isPdfPosterSigned = (contract: PdfContract) =>
  toNonEmptyString(contract.posterSignTime).length > 0;

export const isPdfSignerSigned = (contract: PdfContract) =>
  toNonEmptyString(contract.signTime).length > 0;

const isPdfFullySigned = (contract: PdfContract) =>
  isPdfPosterSigned(contract) && isPdfSignerSigned(contract);

const hasCurrentUserSignedPdf = (
  contract: PdfContract,
  currentUserId?: string,
) => {
  if (!currentUserId) return false;
  if (contract.posterId === currentUserId) return isPdfPosterSigned(contract);
  if (contract.signerId === currentUserId) return isPdfSignerSigned(contract);
  return false;
};

export const getQuickContractListStatus = (
  contract: QuickContract,
  currentUserId?: string,
): ContractListStatus => {
  if (isCancelled(contract.status)) return ContractListStatus.cancelled;
  if (!hasCurrentUserSignedQuick(contract, currentUserId)) {
    return ContractListStatus.unsigned;
  }
  if (!isQuickFullySigned(contract)) return ContractListStatus.waitingOther;
  return ContractListStatus.bothSignedLocked;
};

export const getPdfContractListStatus = (
  contract: PdfContract,
  currentUserId?: string,
): ContractListStatus => {
  if (isPdfPreSigned(contract)) return ContractListStatus.offlineSigned;
  if (isCancelled(contract.status ?? 0)) return ContractListStatus.cancelled;
  if (!hasCurrentUserSignedPdf(contract, currentUserId)) {
    return ContractListStatus.unsigned;
  }
  if (!isPdfFullySigned(contract)) return ContractListStatus.waitingOther;
  return ContractListStatus.bothSignedLocked;
};

export const isCurrentUserPoster = (
  posterId: string,
  currentUserId?: string,
) => currentUserId != null && posterId === currentUserId;
